'use client';

import { useCallback, useEffect, useState } from 'react';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

interface Pager<TPage> {
  page: number;
  current: TPage | undefined;
  hasPrev: boolean;
  hasNext: boolean;
  prev: () => void;
  next: () => void;
}

export function useInfinitePager<TPage>(
  query: UseInfiniteQueryResult<InfiniteData<TPage>>,
  resetKey: string,
): Pager<TPage> {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [resetKey]);

  const pages = query.data?.pages ?? [];
  const safePage = Math.min(page, Math.max(1, pages.length));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const prev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const next = useCallback(() => {
    if (safePage < pages.length) {
      setPage(safePage + 1);
      return;
    }
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage().then((r) => {
      if ((r.data?.pages.length ?? 0) > safePage) setPage(safePage + 1);
    });
  }, [safePage, pages.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    page: safePage,
    current: pages[safePage - 1],
    hasPrev: safePage > 1,
    hasNext: safePage < pages.length || !!hasNextPage,
    prev,
    next,
  };
}
